// Kiosk-/PIN-Terminal: Vollbild-Ansicht für das Wandtablet (docs/DESIGN-zeit-kiosk-terminal.md).
//
// Bewusst OHNE AppShell, Navigation, Glocke und Profilmenü — sonst stünde die komplette Anwendung des
// zuletzt Angemeldeten in der Halle offen. Die Route liegt außerhalb von <ProtectedRoute>; authentifiziert
// wird pro Vorgang mit dem Geräte-Token (Header) plus der persönlichen PIN.
//
// Ablauf: Kachel → Aktion → PIN → Bestätigung → harter Reset. Die PIN ist der LETZTE Schritt und löst die
// Buchung direkt aus; sie lebt nur im React-State dieses Vorgangs, nie im Storage.
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client'
import { clearApiCache } from '../offline/swCache'
import { Art13Page } from '../zeiten/Art13Page'
import {
  clearKioskToken, failedQueueSize, fetchRoster, flushKioskQueue, getKioskToken, KioskApiError, kioskStamp,
  queueSize, setKioskToken, type KioskUser, type StampKind,
} from './kioskApi'

/** Nach der Bestätigung zurück auf den Sperrbildschirm. Bewusst eine eigene, kurze Kiosk-Konstante statt der
 *  Werkstatt-Einstellung `autoLogoutMin`: die ist im Frontend nirgends implementiert (TRANSFERPLAN.md:124),
 *  und ein Terminal soll nach Sekunden zurückfallen, nicht nach Minuten. */
const RESET_MS = 8000

/** Ein angefangener, aber liegengelassener Vorgang (Kachel gewählt, halbe PIN getippt) fällt ebenfalls
 *  zurück — sonst tippt der Nächste seine PIN in die Maske des Vorgängers (Review-Fund). */
const ABORT_MS = 30_000

/** Solange etwas in der Warteschlange liegt, regelmäßig erneut versuchen. Das 'online'-Event allein reicht
 *  nicht: es feuert nur beim ÜBERGANG offline→online — nach einem Tablet-Neustart mit bereits stehendem
 *  WLAN käme es nie, und die Stempel lägen für immer da (Review-Fund). */
const RETRY_MS = 60_000

const ACTIONS: { kind: StampKind; label: string; tone: string }[] = [
  { kind: 'kommen', label: 'Kommen', tone: 'an' },
  { kind: 'gehen', label: 'Gehen', tone: 'aus' },
  { kind: 'pause_start', label: 'Pause', tone: 'pause' },
  { kind: 'pause_ende', label: 'Pause beenden', tone: 'an' },
]

const initials = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('')

type Screen =
  | { view: 'pairing' }
  | { view: 'roster' }
  | { view: 'action'; user: KioskUser }
  | { view: 'pin'; user: KioskUser; kind: StampKind }
  | { view: 'done'; headline: string; detail: string; tone: 'ok' | 'warn' }

export function KioskPage(): React.JSX.Element {
  const [screen, setScreen] = useState<Screen>(getKioskToken() ? { view: 'roster' } : { view: 'pairing' })
  const [users, setUsers] = useState<KioskUser[]>([])
  const [deviceLabel, setDeviceLabel] = useState('')
  const [rosterError, setRosterError] = useState<string | null>(null)
  const [pending, setPending] = useState(0)
  const [failed, setFailed] = useState(0)
  const [showPrivacy, setShowPrivacy] = useState(false)
  // Jeder Tastendruck stösst den Abbruch-Timer neu an — wer langsam tippt, soll nicht rausfliegen.
  const [activity, setActivity] = useState(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadRoster = useCallback(async () => {
    if (!getKioskToken()) return
    try {
      const data = await fetchRoster()
      setUsers(data.users)
      setDeviceLabel(data.device)
      setRosterError(null)
    } catch (err) {
      if (err instanceof KioskApiError && err.status === 401) {
        clearKioskToken()
        setScreen({ view: 'pairing' })
      }
      setRosterError('Keine Verbindung — Stempeln wird als Nachtrag erfasst.')
    }
  }, [])

  // Eine liegengebliebene Session hat auf einem Wandtablet nichts verloren: das Gerät wird typischerweise
  // vom Chef eingerichtet, der sich dafür angemeldet hat. Ohne dieses Abmelden würde jeder Vollreload
  // (z. B. über einen Link) die komplette App in der Halle öffnen (Review-HIGH).
  useEffect(() => {
    void api.logout().catch(() => {})
    void Promise.resolve(clearApiCache()).catch(() => {})
  }, [])

  const sync = useCallback(() => {
    void flushKioskQueue().then(() => { setPending(queueSize()); setFailed(failedQueueSize()) })
    void loadRoster()
  }, [loadRoster])

  useEffect(() => {
    setPending(queueSize())
    setFailed(failedQueueSize())
    sync() // Boot-Flush: ohne ihn bliebe eine Warteschlange nach einem Tablet-Neustart für immer liegen.
    const onVisible = () => {
      if (document.visibilityState === 'visible') sync()
    }
    window.addEventListener('online', sync)
    window.addEventListener('focus', sync)
    document.addEventListener('visibilitychange', onVisible)
    const id = setInterval(() => {
      if (queueSize() > 0) sync()
    }, RETRY_MS)
    return () => {
      window.removeEventListener('online', sync)
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(id)
    }
  }, [sync])

  // Harter Rückfall auf den Sperrbildschirm — ein Terminal darf nie auf einem persönlichen Stand
  // stehenbleiben, weder auf der Bestätigung noch auf einem halb ausgefüllten PIN-Feld.
  useEffect(() => {
    if (screen.view === 'roster' || screen.view === 'pairing') return
    const delay = screen.view === 'done' ? RESET_MS : ABORT_MS
    timer.current = setTimeout(() => setScreen({ view: 'roster' }), delay)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [screen, activity])

  if (screen.view === 'pairing') {
    return <PairingScreen onPaired={() => { setScreen({ view: 'roster' }); void loadRoster() }} />
  }

  return (
    <main className="wf-kiosk" data-theme="dark">
      <header className="wf-kiosk__bar">
        <span className="wf-kiosk__device">{deviceLabel || 'Terminal'}</span>
        <Clock />
        {pending > 0 && <span className="wf-kiosk__pending">{pending} wartet auf Übertragung</span>}
        {failed > 0 && <span className="wf-kiosk__error">{failed} konnte nicht übertragen werden — bitte dem Chef sagen</span>}
      </header>

      {screen.view === 'roster' && (
        <>
          <h1 className="wf-kiosk__title">Wer stempelt?</h1>
          {rosterError && <p className="wf-kiosk__hint">{rosterError}</p>}
          <div className="wf-kiosk__grid">
            {users.map((u) => (
              <button key={u.id} type="button" className="wf-kiosk__tile" onClick={() => setScreen({ view: 'action', user: u })}>
                <span className="wf-kiosk__avatar" style={{ background: u.color }}>{initials(u.name)}</span>
                <span className="wf-kiosk__name">{u.name}</span>
              </button>
            ))}
          </div>
          {users.length === 0 && !rosterError && (
            <p className="wf-kiosk__hint">
              Noch niemand hat eine Terminal-PIN eingerichtet. Das geht in der App unter „Konto“.
            </p>
          )}
          <footer className="wf-kiosk__foot">
            <button type="button" className="wf-kiosk__link" onClick={() => setShowPrivacy(true)}>
              Datenschutz-Hinweise zur Zeiterfassung
            </button>
          </footer>
        </>
      )}

      {screen.view === 'action' && (
        <ActionScreen user={screen.user} onPick={(kind) => setScreen({ view: 'pin', user: screen.user, kind })} onBack={() => setScreen({ view: 'roster' })} />
      )}

      {screen.view === 'pin' && (
        <PinScreen
          user={screen.user}
          kind={screen.kind}
          onBack={() => setScreen({ view: 'action', user: screen.user })}
          onActivity={() => setActivity((n) => n + 1)}
          onDone={(headline, detail, tone) => {
            setPending(queueSize())
            setScreen({ view: 'done', headline, detail, tone })
          }}
        />
      )}

      {showPrivacy && (
        <div className="wf-kiosk__overlay" role="dialog" aria-label="Datenschutz-Hinweise">
          <div className="wf-kiosk__overlay-inner">
            <Art13Page />
            <button type="button" className="wf-kiosk__key" onClick={() => setShowPrivacy(false)}>Schliessen</button>
          </div>
        </div>
      )}

      {screen.view === 'done' && (
        <div className={`wf-kiosk__done wf-kiosk__done--${screen.tone}`} role="status">
          <p className="wf-kiosk__done-head">{screen.headline}</p>
          <p className="wf-kiosk__done-detail">{screen.detail}</p>
          <button type="button" className="wf-kiosk__key" onClick={() => setScreen({ view: 'roster' })}>Fertig</button>
        </div>
      )}
    </main>
  )
}

function Clock(): React.JSX.Element {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 10_000)
    return () => clearInterval(id)
  }, [])
  return <span className="wf-kiosk__clock">{now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
}

function ActionScreen({ user, onPick, onBack }: { user: KioskUser; onPick: (k: StampKind) => void; onBack: () => void }): React.JSX.Element {
  return (
    <>
      <h1 className="wf-kiosk__title">
        {user.name}
        <button type="button" className="wf-kiosk__back" onClick={onBack}>nicht du? Zurück</button>
      </h1>
      <div className="wf-kiosk__actions">
        {ACTIONS.map((a) => (
          <button key={a.kind} type="button" className={`wf-kiosk__action wf-kiosk__action--${a.tone}`} onClick={() => onPick(a.kind)}>
            {a.label}
          </button>
        ))}
      </div>
    </>
  )
}

function PinScreen({
  user, kind, onBack, onDone, onActivity,
}: {
  user: KioskUser
  kind: StampKind
  onBack: () => void
  onActivity: () => void
  onDone: (headline: string, detail: string, tone: 'ok' | 'warn') => void
}): React.JSX.Element {
  const [pin, setPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const label = ACTIONS.find((a) => a.kind === kind)!.label

  const submit = useCallback(async (value: string) => {
    setBusy(true)
    try {
      const res = await kioskStamp(user.id, value, kind)
      const zeit = new Date(res.bookedAt ?? res.serverNow).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      if (res.converted === 'correction') {
        onDone(`${res.name}: als Nachtrag eingereicht`, 'Der Chef muss den Eintrag noch bestätigen.', 'warn')
      } else {
        onDone(`${res.name} — ${label} ${zeit}`, statusText(res.status), 'ok')
      }
    } catch (err) {
      if (err instanceof KioskApiError && err.status === 0) {
        onDone(`${user.name}: ohne Verbindung erfasst`, 'Wird nachgereicht und vom Chef bestätigt.', 'warn')
        return
      }
      setPin('')
      setError(err instanceof KioskApiError ? err.message : 'Das hat nicht geklappt')
    } finally {
      setBusy(false)
    }
  }, [user, kind, label, onDone])

  const press = (digit: string) => {
    if (busy) return
    onActivity()
    const next = (pin + digit).slice(0, 6)
    setPin(next)
    setError(null)
    if (next.length === 6) void submit(next)
  }

  return (
    <>
      <h1 className="wf-kiosk__title">
        {user.name} · {label}
        <button type="button" className="wf-kiosk__back" onClick={onBack} disabled={busy}>Zurück</button>
      </h1>
      <p className="wf-kiosk__dots" aria-label={`${pin.length} von 6 Ziffern eingegeben`}>
        {'●'.repeat(pin.length)}{'○'.repeat(6 - pin.length)}
      </p>
      {error && <p className="wf-kiosk__error" role="alert">{error}</p>}
      <div className="wf-kiosk__pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" className="wf-kiosk__key" onClick={() => press(d)} disabled={busy}>{d}</button>
        ))}
        <button type="button" className="wf-kiosk__key wf-kiosk__key--muted" onClick={() => { setPin(''); setError(null) }} disabled={busy}>Löschen</button>
        <button type="button" className="wf-kiosk__key" onClick={() => press('0')} disabled={busy}>0</button>
        <button type="button" className="wf-kiosk__key wf-kiosk__key--muted" onClick={() => setPin((p) => p.slice(0, -1))} disabled={busy}>←</button>
      </div>
    </>
  )
}

function statusText(status: 'an' | 'pause' | 'aus'): string {
  if (status === 'an') return 'Du bist eingestempelt.'
  if (status === 'pause') return 'Du bist in der Pause.'
  return 'Du bist ausgestempelt. Schönen Feierabend!'
}

function PairingScreen({ onPaired }: { onPaired: () => void }): React.JSX.Element {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setKioskToken(code.trim())
    try {
      await fetchRoster()
      onPaired()
    } catch {
      clearKioskToken()
      setError('Dieser Code passt nicht. Bitte in den Einstellungen ein neues Terminal anlegen.')
    }
  }

  return (
    <main className="wf-kiosk wf-kiosk--pairing" data-theme="dark">
      <form className="wf-kiosk__pair" onSubmit={submit}>
        <h1 className="wf-kiosk__title">Terminal einrichten</h1>
        <p className="wf-kiosk__hint">
          Der Chef legt in den Einstellungen unter „Terminals“ ein Gerät an und erhält dabei einmalig einen Code.
        </p>
        <input
          className="wf-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="WF-KIO-XXXX-XXXX-XXXX"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Kopplungs-Code"
        />
        {error && <p className="wf-kiosk__error" role="alert">{error}</p>}
        <button type="submit" className="wf-btn wf-btn--primary" disabled={!code.trim()}>Gerät koppeln</button>
      </form>
    </main>
  )
}
